/**
 * Global Audio Player Hook
 *
 * Manages a SINGLE Howl instance that is truly reused across all songs.
 * Changes the src property when switching songs instead of destroying/recreating.
 * Uses globalAudioSlice for unified Redux state management.
 */

import { useCallback, useEffect } from 'react'
import { Howl } from 'howler'
import { useAppDispatch, useAppSelector } from './useRedux'
import {
  playAudio,
  loadAudio,
  audioLoaded,
  audioLoadFailed,
  pauseAudio,
  setCurrentTime,
  setPlaybackRate as setPlaybackRateAction,
  stopAudio,
  audioEnded,
} from '@/store/slices/globalAudioSlice'

// Single Howl instance - created once, reused forever
let globalHowl: Howl | null = null
let animationFrameId: number | null = null

/**
 * Initialize the global Howl instance (one time only)
 */
function initializeHowl(dispatch: ReturnType<typeof useAppDispatch>) {
  if (globalHowl) return globalHowl

  console.log('[GlobalAudioPlayer] Creating single Howl instance')

  globalHowl = new Howl({
    src: [''], // Empty src initially
    format: ['mp3'],
    html5: true,
    preload: false,
    onload: () => {
      const dur = globalHowl?.duration() || 0
      dispatch(audioLoaded({ duration: dur }))
      console.log('[GlobalAudioPlayer] Song loaded, duration:', dur)
    },
    onloaderror: (_id, error) => {
      console.error('[GlobalAudioPlayer] Load error:', error)
      dispatch(audioLoadFailed())
    },
    onplayerror: (_id, error) => {
      console.error('[GlobalAudioPlayer] Play error:', error)
      dispatch(pauseAudio())
    },
    onplay: () => {
      console.log('[GlobalAudioPlayer] Playing')
    },
    onpause: () => {
      console.log('[GlobalAudioPlayer] Paused')
    },
    onend: () => {
      dispatch(audioEnded())
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    },
    onstop: () => {
      dispatch(pauseAudio())
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    },
  })

  return globalHowl
}

export function useGlobalAudioPlayer() {
  const dispatch = useAppDispatch()
  const globalAudio = useAppSelector((state) => state.globalAudio)

  // Only manage Howler if this is the active player type
  const isHowlerActive = globalAudio.playerType === 'howler'

  // Progress update loop
  const updateProgress = useCallback(() => {
    if (globalHowl && globalHowl.playing()) {
      const time = globalHowl.seek() as number
      dispatch(setCurrentTime(time))
      animationFrameId = requestAnimationFrame(updateProgress)
    }
  }, [dispatch])

  // Initialize Howl on first use
  useEffect(() => {
    initializeHowl(dispatch)
  }, [dispatch])

  // Sync Howler playback with Redux state
  useEffect(() => {
    if (!isHowlerActive || !globalHowl) return

    if (globalAudio.isPlaying) {
      if (!globalHowl.playing()) {
        globalHowl.play()
      }
      updateProgress()
    } else {
      if (globalHowl.playing()) {
        globalHowl.pause()
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }
  }, [globalAudio.isPlaying, isHowlerActive, updateProgress])

  // Stop Howler when switching to different player type
  useEffect(() => {
    if (globalAudio.playerType && globalAudio.playerType !== 'howler' && globalHowl) {
      if (globalHowl.playing()) {
        globalHowl.stop()
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }
  }, [globalAudio.playerType])

  // Play a song (or switch to new song)
  const play = useCallback(
    (songId: string, audioUrl: string, onTrack?: () => void) => {
      const howl = initializeHowl(dispatch)

      // Check if we need to load a new song
      const needsNewSong = globalAudio.currentAudioId !== songId

      if (needsNewSong) {
        console.log('[GlobalAudioPlayer] Switching to new song:', { songId, audioUrl })

        // Stop current playback if any
        if (howl.playing()) {
          howl.stop()
        }

        // Update Redux state - loadAudio will trigger middleware to stop other players
        dispatch(loadAudio({ audioUrl, audioId: songId, playerType: 'howler' }))

        // Change the src of the existing Howl instance
        howl.unload() // Unload current audio data
        // @ts-expect-error Howler.js allows _src to be set directly
        howl._src = audioUrl
        howl.load() // Reload with new src

        // Update playback rate to current setting
        howl.rate(globalAudio.playbackRate)

        // Play when loaded
        howl.once('load', () => {
          dispatch(playAudio({ audioUrl, audioId: songId, playerType: 'howler' }))
          if (onTrack) onTrack()
        })
      } else {
        // Resume current song
        console.log('[GlobalAudioPlayer] Resuming current song')
        dispatch(playAudio({ audioUrl, audioId: songId, playerType: 'howler' }))
        if (onTrack) onTrack()
      }
    },
    [dispatch, globalAudio.currentAudioId, globalAudio.playbackRate]
  )

  // Pause current song
  const pause = useCallback(() => {
    if (globalHowl) {
      globalHowl.pause()
      dispatch(pauseAudio())
    }
  }, [dispatch])

  // Stop and clear
  const stop = useCallback(() => {
    if (globalHowl) {
      globalHowl.stop()
    }
    dispatch(stopAudio())
  }, [dispatch])

  // Seek to specific time
  const seek = useCallback(
    (time: number) => {
      if (globalHowl && globalHowl.state() === 'loaded') {
        globalHowl.seek(time)
        dispatch(setCurrentTime(time))
      }
    },
    [dispatch]
  )

  // Change playback rate
  const changeRate = useCallback(
    (rate: number) => {
      dispatch(setPlaybackRateAction(rate))
      if (globalHowl && globalHowl.state() === 'loaded') {
        globalHowl.rate(rate)
      }
    },
    [dispatch]
  )

  // Check if a specific song is current
  const isCurrentSong = useCallback(
    (songId: string) => {
      return globalAudio.currentAudioId === songId
    },
    [globalAudio.currentAudioId]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [])

  return {
    currentSongId: globalAudio.currentAudioId,
    currentAudioUrl: globalAudio.currentAudioUrl,
    isPlaying: globalAudio.isPlaying && isHowlerActive,
    isLoading: globalAudio.isLoading,
    currentTime: globalAudio.currentTime,
    duration: globalAudio.duration,
    playbackRate: globalAudio.playbackRate,
    play,
    pause,
    stop,
    seek,
    changeRate,
    isCurrentSong,
  }
}
